<script>
import Layout from "@/router/layouts/main";
import UploadZone from "@/components/energia/UploadZone.vue";
import { api } from "@/state/services";

export default {
  components: { Layout, UploadZone },

  data() {
    return {
      loading: true,
      saving: false,
      cliente: null,
      form: {
        tipo_documento: "cnh",
        arquivo_doc_frente: null,
        arquivo_doc_frente_nome: "",
        arquivo_doc_verso: null,
        arquivo_doc_verso_nome: "",
      },
    }
  },

  computed: {
    clientId() {
      return this.$route.params.id
    },

    /**
     * Mesma regra de clientes-detalhe: o último envio prevalece; espelho na ficha só conta sem fila ativa.
     * Não existe na API status por face (frente/verso) — reprovação é sempre do envio inteiro.
     */
    documentStepStatus() {
      const c = this.cliente
      if (!c) {
        return "pendente"
      }
      const last = c.document_last_submission
      if (last) {
        if (last.status === "pending") {
          return "enviado"
        }
        if (last.status === "rejected") {
          return "reprovado"
        }
        if (last.status === "approved") {
          return "aprovado"
        }
      }
      const hasMirror =
        c.document_front_url && String(c.document_front_url).trim() !== ""
      if (hasMirror) {
        return "aprovado"
      }
      return "pendente"
    },

    envioBloqueado() {
      return this.documentStepStatus === "aprovado" || this.documentStepStatus === "enviado"
    },

    mensagemBloqueio() {
      if (this.documentStepStatus === "aprovado") {
        return "O documento pessoal já foi homologado. Não é necessário enviar novos arquivos."
      }
      if (this.documentStepStatus === "enviado") {
        return "Já existe um envio em análise. Aguarde o resultado antes de enviar outro documento."
      }
      return ""
    },
  },

  created() {
    this.carregarCliente()
  },

  methods: {
    carregarCliente() {
      this.loading = true
      api
        .get("energy/clients/" + this.clientId)
        .then((response) => {
          if (response.data.status === "success") {
            this.cliente = response.data.client
            const t = this.cliente.document_type
            if (t === "cnh" || t === "rg") {
              this.form.tipo_documento = t
            }
          } else {
            this.cliente = null
          }
        })
        .catch(() => {
          this.cliente = null
        })
        .finally(() => {
          this.loading = false
        })
    },

    validar() {
      if (!this.form.arquivo_doc_frente) {
        this.$toast.error("Envie o arquivo da frente do documento.")
        return false
      }
      if (this.form.tipo_documento === "rg" && !this.form.arquivo_doc_verso) {
        this.$toast.error("Para RG, o verso do documento é obrigatório.")
        return false
      }
      return true
    },

    enviar() {
      if (this.envioBloqueado) {
        return
      }
      if (!this.validar()) {
        return
      }
      this.saving = true
      const formData = new FormData()
      formData.append("document_type", this.form.tipo_documento)
      formData.append("doc_front", this.form.arquivo_doc_frente)
      if (this.form.arquivo_doc_verso) {
        formData.append("doc_back", this.form.arquivo_doc_verso)
      }

      api
        .post("energy/clients/" + this.clientId + "/documents", formData)
        .then((response) => {
          if (response.data.status === "success") {
            this.$toast.success(
              response.data.message || "Documento enviado com sucesso."
            )
            this.$router.push("/energia/clientes/" + this.clientId)
          } else {
            this.$toast.error(
              response.data.message || "Não foi possível enviar o documento."
            )
          }
        })
        .catch(() => {
          this.$toast.error("Erro ao enviar o documento, tente novamente em instantes.")
        })
        .finally(() => {
          this.saving = false
        })
    },

    onDocUpload(key, file) {
      if (file.size > 100 * 1024 * 1024) {
        this.$toast.error("Arquivo muito grande. Máximo 100MB.")
        return
      }
      this.form[key] = file
      this.form[key + "_nome"] = file.name
    },

    onDocRemover(key) {
      this.form[key] = null
      this.form[key + "_nome"] = ""
    },
  },
}
</script>

<template>
  <Layout>
    <div class="page-title-box d-flex align-items-center mb-3">
      <router-link
        :to="'/energia/clientes/' + clientId"
        class="btn btn-sm btn-light mr-3"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" style="fill: rgba(0, 0, 0, 1);">
          <path d="M12.707 17.293 8.414 13H18v-2H8.414l4.293-4.293-1.414-1.414L4.586 12l6.707 6.707z"></path>
        </svg>
      </router-link>
      <div>
        <h3 class="mb-0">Enviar documento pessoal</h3>
        <p v-if="cliente" class="text-muted mb-0 font-size-13">
          {{ cliente.name }}
        </p>
      </div>
    </div>

    <div v-if="loading" class="text-center py-5 text-muted">
      Carregando…
    </div>

    <div v-else-if="!cliente" class="alert alert-danger">
      Cliente não encontrado.
      <router-link to="/energia/clientes">Voltar à lista</router-link>
    </div>

    <div v-else class="card">
      <div class="card-body">
        <div class="row justify-content-center">
          <div class="col-md-6">
            <div
              v-if="envioBloqueado"
              class="alert mb-4"
              :class="documentStepStatus === 'aprovado' ? 'alert-success' : 'alert-warning'"
            >
              <i class="bx font-size-18 mr-1" :class="documentStepStatus === 'aprovado' ? 'bx-check-circle' : 'bx-time-five'"></i>
              {{ mensagemBloqueio }}
            </div>

            <template v-if="!envioBloqueado">
              <p class="text-muted font-size-13 mb-4">
                Envie os documentos de identificação do titular. Aceito: imagens ou PDF. Máximo 100MB por arquivo.
              </p>

              <div class="btn-group mb-4">
                <button type="button" class="btn" :class="form.tipo_documento === 'cnh' ? 'btn-primary' : 'btn-light'" @click="form.tipo_documento = 'cnh'">
                  <i class="bx bx-card mr-1"></i> CNH
                </button>
                <button type="button" class="btn" :class="form.tipo_documento === 'rg' ? 'btn-primary' : 'btn-light'" @click="form.tipo_documento = 'rg'">
                  <i class="bx bx-id-card mr-1"></i> RG
                </button>
              </div>

              <div class="row mb-4">
                <div class="col-md-6 mb-3">
                  <p class="font-size-13 font-weight-semibold mb-2">
                    Frente <span class="text-danger">*</span>
                  </p>
                  <UploadZone
                    :label="'Frente da ' + (form.tipo_documento === 'cnh' ? 'CNH' : 'RG')"
                    accept="image/*,.pdf"
                    :max-mb="100"
                    :value="form.arquivo_doc_frente_nome"
                    @change="file => onDocUpload('arquivo_doc_frente', file)"
                    @remover="onDocRemover('arquivo_doc_frente')"
                  />
                </div>
                <div class="col-md-6 mb-3">
                  <p class="font-size-13 font-weight-semibold mb-2">
                    Verso
                    <span v-if="form.tipo_documento === 'rg'" class="text-danger">*</span>
                    <span v-else class="text-muted font-size-11">(opcional para CNH)</span>
                  </p>
                  <UploadZone
                    :label="'Verso da ' + (form.tipo_documento === 'cnh' ? 'CNH' : 'RG')"
                    accept="image/*,.pdf"
                    :max-mb="100"
                    :value="form.arquivo_doc_verso_nome"
                    @change="file => onDocUpload('arquivo_doc_verso', file)"
                    @remover="onDocRemover('arquivo_doc_verso')"
                  />
                </div>
              </div>
            </template>

            <hr />
            <div class="d-flex justify-content-between align-items-center">
              <router-link :to="'/energia/clientes/' + clientId" class="btn btn-light">
                {{ envioBloqueado ? 'Voltar ao cliente' : 'Cancelar' }}
              </router-link>
              <button
                v-if="!envioBloqueado"
                type="button"
                class="btn btn-primary"
                :disabled="saving"
                @click="enviar"
              >
                Enviar para análise
                <span v-if="saving" class="spinner-border spinner-border-sm ml-1"></span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>
